import { useState, useEffect } from "react";
import { Radar, RefreshCw, TrendingUp, TrendingDown, Target, Bookmark, CheckCircle2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const TAG_STYLES = {
  TOP_PICK: { label: "TOP PICK", bg: "bg-emerald-500/20", border: "border-emerald-400/50", text: "text-emerald-300" },
  QUALITY: { label: "QUALITY", bg: "bg-cyan-500/15", border: "border-cyan-400/40", text: "text-cyan-300" },
  WATCH: { label: "WATCH", bg: "bg-amber-500/10", border: "border-amber-400/30", text: "text-amber-300" },
};

export default function SmartScreener({ onTickerClick, portfolioTickers = [], owner }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterTag, setFilterTag] = useState("ALL");
  const [search, setSearch] = useState("");
  const [autoStatus, setAutoStatus] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoFlash, setAutoFlash] = useState(null);

  const fetchData = async (refresh = false) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/smart-screener/lt`, {
        params: { ...(refresh ? { refresh: true } : {}), limit: 30 },
        timeout: 180000,
      });
      setData(r.data);
    } catch (e) {
      console.error("Smart screener error", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAutoStatus = async () => {
    if (!owner) return;
    try {
      const r = await axios.get(`${API}/api/smart-screener/auto-watchlist/status`, { params: { owner }, timeout: 8000 });
      setAutoStatus(r.data);
    } catch (e) { /* silent */ }
  };

  const runAutoWatchlist = async () => {
    if (!owner) return;
    setAutoLoading(true);
    setAutoFlash(null);
    try {
      const r = await axios.post(`${API}/api/smart-screener/auto-watchlist/run`, null, {
        params: { owner, top_n: 5 },
        timeout: 60000,
      });
      setAutoFlash({
        added: r.data?.total_added || 0,
        skipped: r.data?.total_skipped || 0,
        addedList: r.data?.added || [],
      });
      fetchAutoStatus();
    } catch (e) {
      setAutoFlash({ error: e.response?.data?.detail || e.message });
    } finally {
      setAutoLoading(false);
    }
  };

  useEffect(() => { fetchData(false); fetchAutoStatus(); /* eslint-disable-next-line */ }, [owner]);

  const ownedSet = new Set((portfolioTickers || []).map((t) => (t || "").toUpperCase()));

  const filtered = (data?.candidates || []).filter((c) => {
    if (filterTag !== "ALL" && c.tag !== filterTag) return false;
    if (search && !c.ticker.toLowerCase().includes(search.toLowerCase()) && !(c.theme || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="glass-card rounded-xl border border-violet-500/25 overflow-hidden" data-testid="smart-screener-panel">
      <div className="bg-gradient-to-r from-violet-600/15 via-purple-600/10 to-transparent px-5 py-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-violet-500/20 border border-violet-400/30">
            <Radar size={18} className="text-violet-300" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base">Smart Screener LT</h3>
            <p className="text-slate-400 text-xs">
              Scan auto univers US+EU · Score multi-facteurs (valo + qualité + drawdown + tendance)
            </p>
          </div>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={loading}
          className="p-2 rounded-lg bg-slate-700/60 hover:bg-slate-600 text-slate-300 transition disabled:opacity-50"
          data-testid="smart-screener-refresh-btn"
          title="Forcer le re-scan (lent ~30s)"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Auto-Watchlist banner */}
      {owner && (
        <div className="px-4 py-2.5 bg-gradient-to-r from-emerald-900/20 via-emerald-800/10 to-transparent border-b border-emerald-500/20 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Bookmark size={14} className="text-emerald-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-xs text-emerald-200 font-semibold">Auto-Watchlist hebdo</div>
              <div className="text-[10px] text-slate-400 truncate">
                {autoStatus?.last_run_at
                  ? `Dernier run : ${new Date(autoStatus.last_run_at).toLocaleString("fr-FR")} · ${autoStatus.auto_added_count || 0} tickers ajoutés`
                  : "Top 5 ajoutés automatiquement chaque semaine à votre watchlist"}
              </div>
              {autoFlash && (
                <div className={`text-[10px] mt-0.5 ${autoFlash.error ? "text-rose-400" : "text-emerald-300"}`}>
                  {autoFlash.error ? `Erreur: ${autoFlash.error}` :
                    `✓ ${autoFlash.added} ajoutés${autoFlash.skipped ? ` · ${autoFlash.skipped} déjà présents` : ""}${
                      autoFlash.addedList?.length ? ` (${autoFlash.addedList.map(a => a.ticker).join(", ")})` : ""
                    }`}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={runAutoWatchlist}
            disabled={autoLoading}
            className="px-3 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 text-emerald-200 text-xs font-semibold transition disabled:opacity-50 flex items-center gap-1.5 shrink-0"
            data-testid="auto-watchlist-run-btn"
          >
            {autoLoading ? <RefreshCw size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
            {autoLoading ? "Ajout en cours…" : "Run maintenant"}
          </button>
        </div>
      )}

      {/* Filters */}
      {data && (
        <div className="px-4 py-2 border-b border-slate-700/50 bg-slate-900/40 flex flex-wrap items-center gap-2">
          <div className="flex gap-1">
            {["ALL", "TOP_PICK", "QUALITY", "WATCH"].map((t) => (
              <button
                key={t}
                onClick={() => setFilterTag(t)}
                className={`px-2.5 py-1 rounded text-[11px] font-semibold transition ${
                  filterTag === t
                    ? "bg-violet-500/25 text-violet-200 border border-violet-400/40"
                    : "bg-slate-800/40 text-slate-400 hover:bg-slate-700/50"
                }`}
                data-testid={`screener-filter-${t}`}
              >
                {t === "ALL" ? `TOUT (${data.summary?.qualified || 0})` :
                 t === "TOP_PICK" ? `🟢 TOP (${data.summary?.top_pick_count || 0})` :
                 t === "QUALITY" ? `🔵 QUALITY (${data.summary?.quality_count || 0})` :
                 `🟡 WATCH (${data.summary?.watch_count || 0})`}
              </button>
            ))}
          </div>
          <input
            placeholder="Filtre ticker / theme..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ml-auto px-2.5 py-1 rounded text-[11px] bg-slate-800/60 border border-slate-700 text-slate-200 placeholder-slate-500 outline-none focus:border-violet-400/50 w-44"
            data-testid="screener-search"
          />
        </div>
      )}

      {loading && !data && <div className="p-6 text-center text-slate-400 text-sm">Scan en cours… (~30s)</div>}

      {data && (
        <div className="divide-y divide-slate-700/40">
          {filtered.length === 0 && (
            <div className="p-5 text-center text-slate-500 text-sm">Aucun résultat avec ce filtre.</div>
          )}
          {filtered.slice(0, 30).map((c, i) => {
            const style = TAG_STYLES[c.tag] || TAG_STYLES.WATCH;
            const owned = ownedSet.has((c.ticker || "").toUpperCase());
            const dd = c.drawdown_ath || 0;
            const perf3m = c.perf_3m || 0;
            return (
              <div
                key={c.ticker}
                onClick={() => onTickerClick && onTickerClick(c.ticker)}
                className="p-3 hover:bg-slate-800/40 cursor-pointer transition"
                data-testid={`screener-row-${c.ticker}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center font-bold text-sm bg-violet-500/15 border border-violet-400/30 text-violet-200 shrink-0">
                      {c.score}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-bold text-sm">{c.ticker}</span>
                        <span className="text-slate-400 text-xs truncate max-w-xs">{c.name || ""}</span>
                        {owned && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 font-medium">
                            HOLDING
                          </span>
                        )}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${style.bg} ${style.border} ${style.text}`}>
                          {style.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500 flex-wrap">
                        <span>{c.theme}</span>
                        {c.forward_pe && <><span>·</span><span>PE {c.forward_pe}</span></>}
                        {c.roe_pct && <><span>·</span><span>ROE {c.roe_pct}%</span></>}
                        {c.dividend_yield_pct && <><span>·</span><span>Div {c.dividend_yield_pct}%</span></>}
                        {c.market_cap_b && <><span>·</span><span>${c.market_cap_b}B</span></>}
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-white font-semibold text-sm">${c.current_price}</div>
                    <div className={`text-[11px] flex items-center gap-0.5 justify-end ${perf3m >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                      {perf3m >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                      3M {perf3m >= 0 ? "+" : ""}{perf3m.toFixed(1)}%
                    </div>
                    <div className="text-[10px] text-amber-400 mt-0.5">DD {dd.toFixed(0)}%</div>
                  </div>
                </div>
                {c.triggers && c.triggers.length > 0 && (
                  <div className="mt-2 ml-12 flex flex-wrap gap-1">
                    {c.triggers.slice(0, 4).map((t, ti) => (
                      <span key={ti} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800/60 border border-slate-700/60 text-slate-400">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {data && (
        <div className="px-4 py-2 text-[10px] text-slate-500 bg-slate-800/30 border-t border-slate-700/50 flex items-center gap-2">
          <Target size={11} />
          <span>Univers : {data.summary?.total_universe} tickers · Qualifiés : {data.summary?.qualified} · Cache 6h · Click un ticker pour analyse Algo + Smart Analyzer</span>
        </div>
      )}
    </div>
  );
}
